import React, { PureComponent, type RefObject } from 'react';
import { type ConnectedProps, connect } from 'react-redux';

import classNames from 'classnames';
import { isEqual } from 'lodash-es';

import { AI_GENERATED } from '@iconik-io/iconik-api/src/schema_patched/metadata';
import type {
  MetadataFieldOptionsSchema,
  MetadataMetadataFieldSchema,
} from '@iconik-io/iconik-api/src/services/metadata';
import { isMetadataFieldType } from '@iconik-io/iconik-api/src/types/metadata_field_type';
import { OBJECT_TYPE_SEGMENTS } from '@iconik-io/iconik-api/src/types/object_types';
import { Icon } from '@iconik-io/uad';

import {
  FIELD_UPDATE_BULK_PREFIX,
  FIELD_UPDATE_MODE_PREFIX,
} from '../../constants/data';
import { FIELDS } from '../../constants/metadata';
import { errorMessage, successMessage } from '../../ducks/messages/thunks';
import { getSearchSuggestions } from '../../ducks/search_suggest/thunks';
import { selectDateFormat } from '../../ducks/settings/select_date_format';
import { selectDateTimeFormat } from '../../ducks/settings/select_date_time_format';
import type { AppState } from '../../store/types';
import { aggregateAiGenerated } from '../../utils/format_metadata_values';
import metadataFieldRenderer, {
  METADATA_EDIT_TRANSITION_TIME,
  type MetadataFieldRenderer,
} from '../metadata_form_field/metadata_form_field';
import type { MetadataFieldSearchRenderer } from '../metadata_form_field/search';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';

import styles from './metadata_fields.module.css';

function getAbsoluteHeight(el: HTMLElement): number {
  const style = window.getComputedStyle(el);
  const margin = parseFloat(style.marginTop) + parseFloat(style.marginBottom);

  return Math.ceil(el.offsetHeight + margin);
}

function mapStateToProps(state: AppState) {
  return {
    dateFormat: selectDateFormat(state),
    dateTimeFormat: selectDateTimeFormat(state),
  };
}

const connector = connect(mapStateToProps, {
  getSearchSuggestions,
  successMessage,
  errorMessage,
});

const EMPTY_ARRAY: unknown[] = [];
const EMPTY_OPTIONS: MetadataFieldOptionsSchema[] = [];

const renderAiGeneratedIcon = (value: unknown) => {
  const aiGenerated = Array.isArray(value)
    ? aggregateAiGenerated(value)
    : undefined;
  if (!aiGenerated) {
    return undefined;
  }
  const isFilledByAutomation = aiGenerated === AI_GENERATED;
  const text = isFilledByAutomation
    ? 'This field was filled with AI'
    : 'This field was partially filled with AI';
  return (
    <TooltipTrigger tooltipText={text} placement="left">
      <span
        role="img"
        aria-label={text}
        className={
          isFilledByAutomation ? styles.ai_icon : styles.ai_icon_edited
        }
      >
        <Icon name="ai_metadata" size="xs" aria-hidden />
      </span>
    </TooltipTrigger>
  );
};

type Props = ConnectedProps<typeof connector> & {
  className?: string;
  dateFormat: string;
  dateTimeFormat: string;
  disabled?: boolean;
  fields: MetadataMetadataFieldSchema[];
  fieldRenderer?: MetadataFieldRenderer | MetadataFieldSearchRenderer;
  onLayoutChanged?: () => void;
  prefix: string;
  // TODO fix any, it's here because of MetadataFields usage inconsistencies in filters panel and metadata panel/modal
  values: any;
  vertical?: boolean;
  objectType?: string;
  onModifierValueChange?: (key: string, value: unknown) => void;
};

export class MetadataFields extends PureComponent<Props> {
  static defaultProps = {
    prefix: '',
  };

  rootRef = React.createRef<HTMLDivElement>();

  fieldRefs: RefObject<HTMLElement | null>[] = [];

  renderedFields: MetadataMetadataFieldSchema[] = [];

  componentDidMount() {
    if (this.props.disabled) {
      this.setInitialAnimationState();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.disabled && !this.props.disabled) {
      this.animateToEditMode();
    }

    if (!prevProps.disabled && this.props.disabled) {
      this.animateToViewMode();
    }

    if (!isEqual(prevProps.values, this.props.values)) {
      if (this.props.disabled) {
        this.setInitialAnimationState(true);
      } else {
        this.refreshHeight();
      }
    }

    if (!isEqual(prevProps.fields, this.props.fields)) {
      if (this.props.disabled) {
        this.setInitialAnimationState();
      } else {
        this.refreshHeight();
      }
    }
  }

  componentWillUnmount() {
    this.fieldRefs = [];
    this.renderedFields = [];
  }

  refreshHeight = () => {
    let height = 0;
    this.fieldRefs.forEach((ref) => {
      if (!ref.current) {
        return;
      }
      height += getAbsoluteHeight(ref.current);
    });

    if (this.rootRef.current) {
      this.rootRef.current.style.height = `${height}px`;
    }
  };

  setInitialAnimationState = (out = false) => {
    let offsetY = 0;
    let height = 0;
    this.fieldRefs.forEach((ref, index) => {
      if (!ref.current) {
        return;
      }
      const field = this.renderedFields[index];
      const absoluteHeight = getAbsoluteHeight(ref.current);
      if (this.isFieldHidden(field)) {
        ref.current.style.transform = `translate3d(0, -${offsetY + 20}px, 0)`;
        ref.current.style.opacity = '0';
        ref.current.style.pointerEvents = 'none';
        offsetY += absoluteHeight;
      } else {
        ref.current.style.transform = `translate3d(0, -${offsetY}px, 0)`;
        ref.current.style.opacity = '1';
        height += absoluteHeight;
      }
    });

    if (!out) {
      if (this.rootRef.current) {
        this.rootRef.current.style.height = `${height}px`;
      }
    } else {
      setTimeout(() => {
        if (this.rootRef.current) {
          this.rootRef.current.style.height = `${height}px`;
        }
      }, METADATA_EDIT_TRANSITION_TIME);
    }
  };

  animateToEditMode = () => {
    if (!this.rootRef.current) {
      return;
    }
    this.rootRef.current.style.height = 'auto';

    setTimeout(() => {
      this.fieldRefs.forEach((ref) => {
        if (ref.current) {
          ref.current.style.transition = `transform ${METADATA_EDIT_TRANSITION_TIME}ms ease-in-out, opacity ${
            METADATA_EDIT_TRANSITION_TIME - 50
          }ms ease-in-out`;
        }
      });
      setTimeout(() => {
        this.fieldRefs.forEach((ref, index) => {
          if (!ref.current) {
            return;
          }
          const field = this.renderedFields[index];
          if (this.isFieldHidden(field)) {
            ref.current.style.opacity = '1';
            ref.current.style.pointerEvents = 'auto';
          }
          ref.current.style.transform = 'translate3d(0, 0, 0)';
        });
        this.props.onLayoutChanged?.();
      }, 0);
    }, 0);
  };

  animateToViewMode = () => {
    this.fieldRefs.forEach((ref) => {
      if (!ref.current) {
        return;
      }
      ref.current.style.zIndex = 'auto';
    });

    this.fieldRefs.forEach((ref, index) => {
      if (!ref.current) {
        return;
      }
      const field = this.renderedFields[index];
      if (this.isFieldHidden(field)) {
        ref.current.style.opacity = '1';
      }
      ref.current.style.transform = 'translate3d(0, 0, 0)';
    });

    setTimeout(() => {
      this.fieldRefs.forEach((ref) => {
        if (ref.current) {
          ref.current.style.transition = `transform ${METADATA_EDIT_TRANSITION_TIME}ms ease-in-out, opacity ${
            METADATA_EDIT_TRANSITION_TIME - 50
          }ms ease-in-out`;
        }
      });
      setTimeout(() => {
        this.setInitialAnimationState(true);
      }, 0);
    }, 0);
  };

  isFieldHidden(field: MetadataMetadataFieldSchema) {
    const name = this.props.prefix
      ? `${this.props.prefix}${field.name}`
      : field.name;
    const value = name ? (this.props.values[name] ?? EMPTY_ARRAY) : EMPTY_ARRAY;
    const singleValue =
      Array.isArray(value) && value.length > 0 && value[0]
        ? value[0].value
        : undefined;

    return field.hide_if_not_set && !field.required && !singleValue;
  }

  handleLayoutChange = () => {
    setTimeout(() => {
      this.refreshHeight();
      setTimeout(() => this.props.onLayoutChanged?.(), 0);
    }, 0);
  };

  render() {
    this.fieldRefs = [];
    this.renderedFields = [];
    return (
      <div
        className={classNames(styles.root, this.props.className)}
        ref={this.rootRef}
      >
        {this.props.fields.map((field, fieldIndex) => {
          const hideMappedField =
            this.props.objectType === OBJECT_TYPE_SEGMENTS &&
            field.mapped_field_name;
          if (hideMappedField || !field.name) {
            return null;
          }

          const ref = React.createRef<HTMLDivElement>();
          this.fieldRefs.push(ref);
          this.renderedFields.push(field);

          const name = this.props.prefix
            ? `${this.props.prefix}${field.name}`
            : field.name;
          const value = this.props.values[name] || EMPTY_ARRAY;

          const fieldRenderer =
            this.props.fieldRenderer ?? metadataFieldRenderer;

          const isMulti = Boolean(field.multi);

          const readOnly = Boolean(
            field.read_only || this.props.disabled || field.mapped_field_name,
          );

          const fieldType = isMetadataFieldType(field.field_type)
            ? field.field_type
            : 'string';
          const control = fieldRenderer(fieldType, {
            label: field.label ?? undefined,
            id: name,
            name,
            value,
            vertical: Boolean(this.props.vertical),
            options: field.options ?? EMPTY_OPTIONS,
            multi: isMulti,
            withCheckboxSelection: isMulti,
            required: Boolean(field.required),
            sortable: Boolean(field.sortable),
            help: field.description,
            readOnly,
            onChange: () => this.handleLayoutChange(),
            onAfterValidation: this.handleLayoutChange,
            hideIfNotSet: Boolean(field.hide_if_not_set && !field.required),
            getSearchSuggestions: this.props.getSearchSuggestions,
            dateFormat: this.props.dateFormat,
            dateTimeFormat: this.props.dateTimeFormat,
            modifierValues: {
              [FIELD_UPDATE_MODE_PREFIX]:
                this.props.values[FIELD_UPDATE_MODE_PREFIX + name],
              [FIELD_UPDATE_BULK_PREFIX]:
                this.props.values[FIELD_UPDATE_BULK_PREFIX + name],
            },
            onModifierValueChange: this.props.onModifierValueChange,
            onValuesCopied: () =>
              this.props.successMessage({ text: 'Values copied' }),
            onCopyError: (text) => this.props.errorMessage({ text }),
            minValue: field.min_value,
            maxValue: field.max_value,
            labelAdornment: renderAiGeneratedIcon(value),
          });

          const key =
            field.field_type === FIELDS.SEPARATOR
              ? `separator_${fieldIndex}`
              : name;
          return (
            <div className={styles.field} ref={ref} key={key}>
              {control}
            </div>
          );
        })}
      </div>
    );
  }
}

export default connector(MetadataFields);
